import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import pick from 'lodash/pick';
import { useDataLayer } from 'hooks/useDataLayer';
import isNumber from 'lodash/isNumber';
import { Flex, Button } from 'roo-ui/components';
import { mediaQuery } from 'lib/styledSystem';
import createSearchQueryString from 'lib/search/createSearchQueryString';
import { getQuery } from 'store/search/searchSelectors/querySelectors';
import AppLink from 'components/AppLink';
import { useTrackSplitEvent } from 'hooks/useTrackSplitEvent';

const ButtonLink = styled(Button)`
  flex-grow: 1;
  font-size: ${themeGet('fontSizes.base')};
  padding-top: 10px;
  padding-bottom: 10px;
  ${mediaQuery.minWidth.md} {
    padding: ${themeGet('space.2')} ${themeGet('space.6')};
    flex-grow: initial;
  }

  &:hover {
    color: ${themeGet('colors.white')};
  }
`;

ButtonLink.displayName = 'ButtonLink';

const checkoutQueryString = ({ query, roomTypeId, propertyId, offerId, payWith, initialCash }) => {
  const baseCheckoutQuery = {
    roomTypeId,
    propertyId,
    offerId,
    ...pick(query, ['checkIn', 'checkOut', 'adults', 'children', 'infants']),
  };

  return createSearchQueryString({
    ...baseCheckoutQuery,
    initialCash: isNumber(initialCash) ? initialCash : undefined,
    payWith,
  });
};

const OfferCheckoutLink = ({ propertyId, roomTypeId, offerId, offerName, initialCash, payWith, roomName }) => {
  const query = useSelector(getQuery);
  const trackSplitEvent = useTrackSplitEvent();

  const checkoutUrl = `/checkout?${checkoutQueryString({
    query,
    initialCash,
    roomTypeId,
    propertyId,
    offerId,
    payWith: payWith || query.payWith,
  })}`;
  const { emitInteractionEvent } = useDataLayer();

  const handleOnClick = useCallback(() => {
    emitInteractionEvent({ type: 'Select Offer Button', value: `${offerName}` });
    trackSplitEvent({
      name: 'navigate_from_property_to_checkout',
      value: null,
    });
  }, [emitInteractionEvent, offerName, trackSplitEvent]);

  return (
    <Flex justifyContent={['flex-start', 'flex-end']} mt={2}>
      <ButtonLink
        as={AppLink}
        to={checkoutUrl}
        variant="primary"
        data-testid="offer-checkout-link"
        onClick={handleOnClick}
        aria-label={`Select ${roomName}, ${offerName}`}
      >
        Select
      </ButtonLink>
    </Flex>
  );
};

OfferCheckoutLink.propTypes = {
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerName: PropTypes.string.isRequired,
  initialCash: PropTypes.number,
  payWith: PropTypes.string,
  roomName: PropTypes.string.isRequired,
};

OfferCheckoutLink.defaultProps = {
  initialCash: null,
  payWith: null,
};

export default OfferCheckoutLink;
